import { useEffect, useState, useMemo } from "react";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useContext } from "react";
import { HostPortalContext } from "@/contexts/portalBranding";
import { PoweredByReveshare } from "@/components/common/PoweredByReveshare";
import { Helmet } from "react-helmet-async";
import { brandService } from "../../services/brand.service";
import { Facebook, Instagram, Youtube, ArrowLeft, X } from "react-feather";
import TikTokIcon from "../../assets/tiktok";
import { DiscordIcon } from "../../assets/discord";
import { ShowcaseCarousel } from "../../components/ShowcaseCarousel";
import { ProductShowcase } from "../../components/ProductShowcase";
import type { BrandDetails, AppearanceSettings } from "../../types/brand";
import { ICON_SIZES } from "../../types/brand";
import { JoinBrandModal } from "../../components/affiliate/JoinBrandModal";
import {
  programService,
  JoinProgramResponse,
} from "../../services/program.service";
import { toast } from "react-hot-toast";
import { formatSocialMediaUrl } from "../../utils/socialMediaUtils";
import { useAuth } from "../../hooks/useAuth";
import {
  paymentMethodsService,
  BrandSupportedPaymentMethod,
} from "../../services/paymentMethods.service";
import { Dialog } from "@headlessui/react";
import {
  getThemeLayout,
  getThemeStyles,
  processYouTubeContent,
  getAppearanceClasses,
  getColors,
} from "../../utils/themeUtils";
import { useMediaQuery } from "react-responsive";
import { formatstorageUrl } from "@/utils/formatters";
import {
  readProgramToken,
  stripProgramTokenFromSearch,
  withProgramToken,
} from "../../utils/programShareLink";
import { DEFAULT_BRAND } from "../../theme/brandTheme";
import { useBrandTheme } from "../../theme/useBrandTheme";
import { CustomJoinFrame } from "../../components/customJoinPage/CustomJoinFrame";
import { CustomJoinPageStrip } from "../../components/customJoinPage/CustomJoinPageStrip";
import type { JoinFrameState } from "../../types/customJoinPage";
import { Spinner } from "@/components/ui/spinner";

import { AD_REQUEST_TOKEN_KEY } from "@/services/affiliateAdRequest.service";
export function PublicBrandDetailView() {
  // Two ways in: `/store/:username/join` on the marketplace, and `/join` on a
  // brand's own portal, where the subdomain identifies the brand and a
  // username in the path would be redundant.
  const { username: usernameParam } = useParams();
  const { branding: hostBranding } = useContext(HostPortalContext);
  const username = usernameParam ?? hostBranding?.brandUsername ?? undefined;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  // `?p=<token>` selects one of the brand's programs. Absent → default program.
  const programToken = readProgramToken(searchParams);
  const [brandData, setBrandData] = useState<BrandDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasJoined, setHasJoined] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [hasPendingApplication, setHasPendingApplication] = useState(false);
  const [hasPendingInvitation, setHasPendingInvitation] = useState(false);
  const [appearanceSettings, setAppearanceSettings] =
    useState<AppearanceSettings | null>(null);
  const isMobile = useMediaQuery({ query: "(max-width: 1366px)" });

  // Payment method states
  const [supportedPaymentMethods, setSupportedPaymentMethods] = useState<
    BrandSupportedPaymentMethod[]
  >([]);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [defaultCode, setDefaultCode] = useState("");
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  // Get CSS classes based on appearance settings
  const appearanceClasses = useMemo(() => {
    return getAppearanceClasses(appearanceSettings);
  }, [appearanceSettings]);

  const socialMediaIconSize = useMemo(() => {
    return ICON_SIZES.find(
      (size) =>
        size.label.toUpperCase() === appearanceSettings?.socialMediaIconSizes
    )?.size;
  }, [appearanceSettings?.socialMediaIconSizes]);

  // Get color values from appearance settings
  const colors = useMemo(() => {
    return getColors(appearanceSettings);
  }, [appearanceSettings]);

  // Apply the brand's colors to the brand-* tokens while this public page is
  // shown; reset on leave so they don't bleed into the rest of the app.
  useBrandTheme(appearanceSettings, true);

  // Get theme-specific layout and styles
  const themeLayout = useMemo(() => {
    const theme = appearanceSettings?.theme || "TYPE_1";
    return getThemeLayout(theme);
  }, [appearanceSettings?.theme]);

  const themeStyles = useMemo(() => {
    const theme = appearanceSettings?.theme || "TYPE_1";
    return getThemeStyles(theme, colors);
  }, [appearanceSettings?.theme, colors]);

  // A TikTok ad-request link (`?adRequest=<token>`) must survive sign-in and
  // the hop to the affiliate's brand page, which is where the join happens.
  useEffect(() => {
    const token = searchParams.get("adRequest");
    if (token) sessionStorage.setItem(AD_REQUEST_TOKEN_KEY, token);
  }, [searchParams]);

  useEffect(() => {
    const fetchBrandData = async () => {
      if (!username) return;

      try {
        setIsLoading(true);
        const data = await brandService.getBrandDetailsByUsername(
          username,
          programToken
        );
        setBrandData(data);

        // Stale/unknown share token: say so, then carry on with the default
        // program. Dropping the param stops the toast repeating on reload and
        // keeps the join request from re-sending a token the API rejected.
        //
        // The fixed toast id collapses duplicates — see the matching note in
        // pages/affiliate/BrandDetails.tsx. Sharing the id across both pages
        // also stops a second toast when the public page hands off to the
        // dashboard one with the token still in the URL.
        if (data.programTokenInvalid) {
          toast.error("Program not found — showing the default instead.", {
            id: "program-token-invalid",
          });
          navigate(
            {
              pathname: window.location.pathname,
              search: stripProgramTokenFromSearch(window.location.search),
            },
            { replace: true }
          );
        }

        setAppearanceSettings(data.brandAppearance);
        if (!data.brandAppearance)
          setAppearanceSettings({
            joinText: data.program?.joinText || "Join the team",
            joinTitle: data.program?.joinTitle || "",
            ambassadorText:
              data.program?.ambassadorText || "Become a brand ambassador",
            theme: "TYPE_1",
            socialMediaIconSizes: "medium",
            primayColour: DEFAULT_BRAND.primary,
            secondaryColour: DEFAULT_BRAND.secondary,
            imageIds: [],
            showcaseProductIds: [],
          });

        // Fetch supported payment methods
        if (data.id) {
          fetchBrandSupportedPaymentMethods(data.id);
        }

        // Check enrollment / application state against the same source of truth
        // the dashboard uses. The previous checks compared an application id to
        // a program id, and a program status ("ACTIVE") to an enrollment status
        // ("APPROVED") — both always false, so this page offered "Join" to
        // affiliates who were already enrolled.
        if (isAuthenticated && user?.id && data.program?.id) {
          try {
            // `joined` means "in THIS program". Being in another program of the
            // same brand arrives as `switchFrom` and is handled by the join
            // modal, so the Join button stays live.
            const joinStatus = await programService.checkIfJoined(
              data.program.id
            );
            setHasJoined(joinStatus.joined);
            setHasPendingApplication(joinStatus.pendingApplication);
          } catch (error) {
            console.error("Error checking program status:", error);
          }
        }

        // Check for pending invitation if email is in URL params
        const emailParam = searchParams.get("email");
        if (emailParam && data.program?.id) {
          try {
            const invitationCheck = await programService.checkPendingInvitation(
              data.program.id,
              emailParam
            );
            setHasPendingInvitation(invitationCheck.hasPendingInvitation);
          } catch (error) {
            console.error("Error checking pending invitation:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching brand data:", error);
        toast.error("Failed to load brand details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrandData();
  }, [username, isAuthenticated, user?.id, searchParams, programToken, navigate]);

  useEffect(() => {
    console.log(
      "appearanceSettings?.socialMediaIconSizes",
      appearanceSettings?.socialMediaIconSizes
    );
    console.log(
      ICON_SIZES.find(
        (size) =>
          size.label.toUpperCase() === appearanceSettings?.socialMediaIconSizes
      )?.size
    );
  }, [appearanceSettings?.socialMediaIconSizes]);

  // Fetch brand supported payment methods
  const fetchBrandSupportedPaymentMethods = async (brandId: string) => {
    try {
      const methods =
        await paymentMethodsService.getBrandSupportedPaymentMethods(brandId);
      setSupportedPaymentMethods(methods);
    } catch (error) {
      console.error("Error fetching brand supported payment methods:", error);
    }
  };

  // Where a visitor lands once they're signed in. The share token has to ride
  // along or the affiliate would be dropped onto the default program after
  // logging in, having clicked a link for a different one.
  const dashboardBrandUrl = withProgramToken(
    `/affiliate/brands/${username}`,
    brandData?.isSharedProgram ? programToken : undefined
  );

  const handleJoinClick = async () => {
    if (!brandData) return;

    if (!isAuthenticated) {
      // Store the brand data and return URL in sessionStorage
      if (brandData?.id) {
        sessionStorage.setItem("pendingJoinBrandId", brandData.id);
      }
      if (brandData?.companyName) {
        sessionStorage.setItem("pendingJoinBrandName", brandData.companyName);
      }
      if (username) {
        sessionStorage.setItem("pendingJoinBrandUsername", username);
      }
      sessionStorage.setItem(
        "pendingJoinBrandPaymentMethods",
        JSON.stringify(supportedPaymentMethods)
      );
      sessionStorage.setItem("shouldAutoJoin", "true");

      // Show auth modal instead of direct redirect
      sessionStorage.setItem("brandJoinReturnUrl", dashboardBrandUrl);
      setShowAuthModal(true);
      return;
    }

    // If user is already logged in, redirect directly to the brand detail page in dashboard
    if (isAuthenticated && user) {
      navigate(
        withProgramToken(
          `/affiliate/brands/${brandData.username}`,
          brandData.isSharedProgram ? programToken : undefined
        )
      );
      return;
    }

    try {
      setIsGeneratingCode(true);
      const code = await programService.generateCode(brandData.program.id);
      setDefaultCode(code);
      setShowJoinModal(true);
    } catch (error) {
      console.error("Failed to generate affiliate code:", error);
      toast.error("Failed to generate affiliate code");
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleJoinSuccess = (_response: JoinProgramResponse) => {
    setHasJoined(_response.data.status === "APPROVED");
    setHasPendingApplication(_response.data.status === "PENDING");
    setShowJoinModal(false);
    toast.success("Successfully joined the program!");

    // Redirect to affiliate dashboard
    navigate("/affiliate/dashboard");
  };

  const handleBackClick = () => {
    navigate("/");
  };

  // Which program the visitor is about to join. Only worth showing when the
  // link named one — the default program's card already reads as "the" program.
  const programBadge =
    brandData?.isSharedProgram && brandData?.program?.name ? (
      <div
        className="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full text-xs font-medium"
        style={{
          backgroundColor: `${colors.primary}15`,
          color: colors.primary,
        }}
      >
        <span className="opacity-70">Program</span>
        <span>{brandData.program.name}</span>
      </div>
    ) : null;

  if (isLoading) {
    return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
    );
  }

  if (!brandData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2">Brand not found</h1>
          <p className="text-muted-foreground mb-4">
            The brand you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={handleBackClick}
            className="text-brand-600 hover:text-brand-700 flex items-center gap-1 mx-auto"
          >
            <ArrowLeft size={16} />
            Go back
          </button>
        </div>
      </div>
    );
  }

  const head = (
    <Helmet>
      <title>
        {String(brandData.metaTitle || `${brandData.companyName || 'Brand'} - Join Our Affiliate Program | Reveshare`)}
      </title>
      <meta
        name="description"
        content={String(brandData.metaDescription || brandData.description || `Join ${brandData.companyName || 'our'} affiliate program and start earning commissions.`)}
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={String(window.location.href)} />
      <meta
        property="og:title"
        content={String(brandData.metaTitle || `${brandData.companyName || 'Brand'} - Join Our Affiliate Program`)}
      />
      <meta
        property="og:description"
        content={String(brandData.metaDescription || brandData.description || `Join ${brandData.companyName || 'our'} affiliate program and start earning commissions.`)}
      />
      <meta
        property="og:image"
        content={String(brandData.metaImage || brandData.brandBannerImage || brandData.brandProfileImage || '/default-og-image.jpg')}
      />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={String(window.location.href)} />
      <meta
        property="twitter:title"
        content={String(brandData.metaTitle || `${brandData.companyName || 'Brand'} - Join Our Affiliate Program`)}
      />
      <meta
        property="twitter:description"
        content={String(brandData.metaDescription || brandData.description || `Join ${brandData.companyName || 'our'} affiliate program and start earning commissions.`)}
      />
      <meta
        property="twitter:image"
        content={String(brandData.metaImage || brandData.brandBannerImage || brandData.brandProfileImage || '/default-og-image.jpg')}
      />
    </Helmet>
  );

  const modals = (
    <>
      {/* Join Modal */}
      <JoinBrandModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        defaultCode={defaultCode}
        username={brandData?.username || ""}
        onSuccess={handleJoinSuccess}
        onAlreadyJoined={() => {
          setHasJoined(true);
          setShowJoinModal(false);
          navigate("/affiliate/dashboard");
        }}
        programId={brandData?.program?.id || ""}
        programToken={brandData?.isSharedProgram ? programToken : undefined}
        attributionMethod={brandData?.program?.attributionMethod}
        brandName={brandData?.companyName || undefined}
        shortDiscountUrl={brandData?.shortDiscountUrl}
      />

      {/* Authentication Modal */}
      <Dialog
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        className="relative z-50"
      >
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        {/* Full-screen container for centering */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-lg bg-card p-6 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-lg font-medium">
                Join {brandData?.companyName}
              </Dialog.Title>
              <button
                onClick={() => setShowAuthModal(false)}
                className="text-muted-foreground hover:text-muted-foreground"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-muted-foreground mb-6">
              You need to have an account to join this brand as an affiliate.
              Please login or create a new account to continue.
            </p>

            <div className="flex flex-col gap-3">
              {/* Login Button */}
              <Link
                to={`/auth/login?returnUrl=${encodeURIComponent(
                  dashboardBrandUrl
                )}`}
                className={`w-full text-white py-2 px-4 ${appearanceClasses.borderRadius} ${appearanceClasses.animation} text-center transition-all duration-200`}
                style={{
                  backgroundColor: colors.primary,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = "brightness(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = "brightness(1)";
                }}
              >
                Login to Existing Account
              </Link>

              {/* Sign Up Button */}
              <Link
                to={`/affiliate/auth/signup?returnUrl=${encodeURIComponent(
                  dashboardBrandUrl
                )}`}
                className={`w-full border py-2 px-4 ${appearanceClasses.borderRadius} ${appearanceClasses.animation} text-center transition-all duration-200`}
                style={{
                  borderColor: colors.primary,
                  color: colors.primary,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${colors.primary}10`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                Create New Account
              </Link>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );

  // Brand-authored page (admin-enabled, published by the brand). Everything
  // Reveshare owns stays outside the sandboxed frame: meta tags, the join and
  // auth modals, the identity strip. The frame only asks us to start the
  // join flow, which runs the very same handler as the templated page.
  const frameState: JoinFrameState = hasJoined
    ? "joined"
    : hasPendingApplication
      ? "pending"
      : isGeneratingCode
        ? "loading"
        : "join";

  if (brandData.customJoinPage) {
    const page = brandData.customJoinPage;
    return (
      <div className="min-h-screen bg-background">
        {head}
        <CustomJoinFrame
          html={page.html}
          css={page.css}
          hosts={page.hosts}
          cssVars={{ primary: colors.primary, secondary: colors.secondary }}
          state={frameState}
          onJoin={handleJoinClick}
          title={`${brandData.companyName || "Brand"} affiliate program`}
        />
        <CustomJoinPageStrip
          brandName={brandData.companyName || "This brand"}
          username={brandData.username || username || ""}
        />
        <PoweredByReveshare />
        {modals}
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-muted pb-6 ${
        appearanceClasses?.animation || ""
      }`}
      style={{
        backgroundColor: `${colors.secondary}05`,
      }}
    >
      {head}

      {/* Banner Section */}
      <div className="relative">
        {/* Back Button */}
        <div className="absolute top-8 left-4 z-20">
          <button
            onClick={handleBackClick}
            className={`inline-flex items-center px-3 py-2 text-white rounded-lg ${
              appearanceClasses?.animation || ""
            } ${appearanceClasses?.borderRadius || ""} ${
              appearanceClasses?.shadow || ""
            } hover:shadow-md transition-all duration-200`}
            style={{ backgroundColor: colors.primary }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
        </div>

        {/* Banner Image or Gradient */}
        {brandData.brandBannerImage &&
        brandData.brandBannerImage !== "/default-banner.jpg" ? (
          <div className={`${themeLayout.bannerHeight} w-full relative`}>
            {(brandData.brandBannerImage || brandData.brandBannerImageMobile) && (
            <picture>
              {brandData.brandBannerImageMobile && (
                <source
                  media="(max-width: 768px)"
                  srcSet={formatstorageUrl(brandData.brandBannerImageMobile)}
                />
              )}
              <img
                src={formatstorageUrl(
                  brandData.brandBannerImage || brandData.brandBannerImageMobile || ""
                )}
                alt="Brand Banner"
                className={themeLayout.bannerImage}
              />
            </picture>
          )}
            <div className={themeLayout.bannerOverlay} />
          </div>
        ) : (
          <div
            className={themeLayout.bannerHeight}
            style={{
              background: `linear-gradient(to bottom, ${colors.secondary}, white)`,
            }}
          />
        )}
      </div>

      <main
        className={`${themeLayout.container} ${appearanceClasses.animation}`}
      >
        {/* Profile Section */}
        <div
          className={`${themeLayout.profileSection} ${themeLayout.profilePosition}`}
        >
          {/* Profile Image */}
          <div
            className={`${themeLayout.profileImage} ${appearanceClasses.animation}`}
          >
            <div className={themeLayout.profileImageSize}>
              <div
                className={`w-full h-full p-2 bg-card rounded-full ${
                  appearanceClasses?.shadow || ""
                } ${
                  appearanceClasses?.animation || ""
                } hover:shadow-lg transition-shadow`}
              >
                <BrandLogo
                  src={brandData.brandProfileImage}
                  name={brandData.companyName}
                  primaryColor={colors.primary}
                  className={`w-full h-full rounded-full text-2xl ${
                    appearanceClasses?.animation || ""
                  } hover:scale-105 transition-transform`}
                />
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div
            className={`${themeLayout.profileInfo} ${appearanceClasses.spacing}`}
          >
            {/* Company Name and Niches */}
            <div className={`flex flex-col sm:flex-row sm:items-center gap-4`}>
              <h1
                className={`text-[28px] font-semibold text-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight} ${appearanceClasses.animation} text-center lg:text-left`}
              >
                {brandData.companyName ||
                  (brandData.firstName && brandData.lastName
                    ? brandData.firstName + " " + brandData.lastName
                    : "Brand")}
              </h1>
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                {brandData.niches?.map(
                  (niche: { id: string; name: string }) => (
                    <span
                      key={niche.id}
                      className={`${themeStyles.nicheTag} ${appearanceClasses.animation} transition-colors cursor-pointer`}
                      style={{
                        backgroundColor: `${colors.primary}20`,
                        color: colors.primary,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${colors.primary}30`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = `${colors.primary}20`;
                      }}
                    >
                      {niche.name}
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Website */}
            <div
              className={`mt-2 flex items-center gap-2 justify-center sm:justify-start`}
            >
              <span className="text-muted-foreground">Website:</span>
              {brandData.website && (
                <a
                  href={brandData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-foreground break-all ${appearanceClasses.animation} transition-colors`}
                  style={{ color: "#111827" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#111827";
                  }}
                >
                  {brandData.website.includes(".myshopify.com")
                    ? "Click here"
                    : brandData.website.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>

            {/* Socials */}
            <div
              className={`mt-2 flex flex-wrap items-center gap-4 justify-center sm:justify-start`}
            >
              <span className="text-muted-foreground">Socials</span>
              <div className="flex items-center gap-6">
                {Array.isArray(brandData.socialMedia) &&
                brandData.socialMedia.length > 0 ? (
                  brandData.socialMedia.map(
                    (social: {
                      id: string;
                      username: string;
                      socialMediaPlatform: {
                        name: string;
                        url: string;
                      };
                    }) => {
                      const platform = social.socialMediaPlatform.name;
                      // Use the utility function to format the social media URL properly
                      const url =
                        formatSocialMediaUrl(social.username, platform) ||
                        `${social.socialMediaPlatform.url}/${social.username}`;

                      return (
                        <a
                          key={social.id}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${themeStyles.socialIcon} ${appearanceClasses.animation} transition-colors`}
                          style={{ color: "#9CA3AF" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = colors.primary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#9CA3AF";
                          }}
                          title={`${
                            platform.charAt(0) + platform.slice(1).toLowerCase()
                          }: ${social.username}`}
                        >
                          {platform === "FACEBOOK" && (
                            <Facebook
                              className={socialMediaIconSize || "w-5 h-5"}
                            />
                          )}
                          {platform === "INSTAGRAM" && (
                            <Instagram
                              className={socialMediaIconSize || "w-5 h-5"}
                            />
                          )}
                          {platform === "TIKTOK" && (
                            <TikTokIcon
                              className={socialMediaIconSize || "w-5 h-5"}
                            />
                          )}
                          {platform === "YOUTUBE" && (
                            <Youtube
                              className={socialMediaIconSize || "w-5 h-5"}
                            />
                          )}
                          {platform === "DISCORD" && (
                            <DiscordIcon
                              className={socialMediaIconSize || "w-5 h-5"}
                            />
                          )}
                        </a>
                      );
                    }
                  )
                ) : brandData.social ? (
                  <>
                    {brandData.social?.facebook && (
                      <a
                        href={formatSocialMediaUrl(
                          brandData.social.facebook || "",
                          "facebook"
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${themeStyles.socialIcon} ${appearanceClasses.animation} transition-colors`}
                        style={{ color: "#9CA3AF" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = colors.primary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#9CA3AF";
                        }}
                      >
                        <Facebook
                          className={
                            ICON_SIZES.find(
                              (size) =>
                                size.label.toUpperCase() ===
                                appearanceSettings?.socialMediaIconSizes
                            )?.size || "w-5 h-5"
                          }
                        />
                      </a>
                    )}
                    {brandData.social?.instagram && (
                      <a
                        href={formatSocialMediaUrl(
                          brandData.social.instagram || "",
                          "instagram"
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${themeStyles.socialIcon} ${appearanceClasses.animation} transition-colors`}
                        style={{ color: "#9CA3AF" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = colors.primary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#9CA3AF";
                        }}
                      >
                        <Instagram
                          className={
                            ICON_SIZES.find(
                              (size) =>
                                size.label.toUpperCase() ===
                                appearanceSettings?.socialMediaIconSizes
                            )?.size || "w-5 h-5"
                          }
                        />
                      </a>
                    )}
                    {brandData.social?.youtube && (
                      <a
                        href={formatSocialMediaUrl(
                          brandData.social.youtube || "",
                          "youtube"
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${themeStyles.socialIcon} ${appearanceClasses.animation} transition-colors`}
                        style={{ color: "#9CA3AF" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = colors.primary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#9CA3AF";
                        }}
                      >
                        <Youtube
                          className={
                            ICON_SIZES.find(
                              (size) =>
                                size.label.toUpperCase() ===
                                appearanceSettings?.socialMediaIconSizes
                            )?.size || "w-5 h-5"
                          }
                        />
                      </a>
                    )}
                    {brandData.social?.tiktok && (
                      <a
                        href={formatSocialMediaUrl(
                          brandData.social.tiktok || "",
                          "tiktok"
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${themeStyles.socialIcon} ${appearanceClasses.animation} transition-colors`}
                        style={{ color: "#9CA3AF" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = colors.primary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#9CA3AF";
                        }}
                      >
                        <TikTokIcon
                          className={
                            ICON_SIZES.find(
                              (size) =>
                                size.label.toUpperCase() ===
                                appearanceSettings?.socialMediaIconSizes
                            )?.size || "w-5 h-5"
                          }
                        />
                      </a>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    No social media links available
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Rest of content */}
        <div
          className={`${themeLayout.contentGrid}`}
        >
          {/* Content Section */}
          
            <div className={`${themeLayout.description} ${themeLayout.statsPosition}`}>
              {/* Stats */}
              <div
                className={`${themeStyles.statsContainer} ${appearanceClasses.spacing}`}
              >
                <div className={themeLayout.statsGrid}>
                  <div
                    className={`${themeStyles.statsCard} ${appearanceClasses.animation} hover:scale-105 transition-transform`}
                  >
                    <div
                      className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      style={{ color: colors.primary }}
                    >
                      #{brandData.stats?.rank || "0"}
                    </div>
                    <div className="text-sm text-muted-foreground">Rank</div>
                  </div>
                  <div
                    className={`${themeStyles.statsCard} ${appearanceClasses.animation} hover:scale-105 transition-transform`}
                  >
                    <div
                      className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      style={{ color: colors.primary }}
                    >
                      {brandData.stats?.products || "0"}
                    </div>
                    <div className="text-sm text-muted-foreground">Products</div>
                  </div>
                  <div
                    className={`${themeStyles.statsCard} ${appearanceClasses.animation} hover:scale-105 transition-transform`}
                  >
                    <div
                      className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      style={{ color: colors.primary }}
                    >
                      {Number(brandData.stats?.team || 0) >= 1000
                        ? `${parseFloat((Number(brandData.stats?.team || 0) / 1000).toFixed(1))}K+`
                        : brandData.stats?.team || "0"}
                    </div>
                    <div className="text-sm text-muted-foreground">Team</div>
                  </div>
                  <div
                    className={`${themeStyles.statsCard} ${appearanceClasses.animation} hover:scale-105 transition-transform`}
                  >
                    <div
                      className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      style={{ color: colors.primary }}
                    >
                      {Number(brandData.stats?.orders || 0) >= 1000
                        ? `${parseFloat((Number(brandData.stats?.orders || 0) / 1000).toFixed(1))}K+`
                        : brandData.stats?.orders || "0"}
                    </div>
                    <div className="text-sm text-muted-foreground">Orders</div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {brandData.description && (
                <div
                  className={`${themeStyles.descriptionContainer} ${themeLayout.descriptionPosition}`}
                >
                  <div
                    className={`text-muted-foreground prose max-w-none ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight} ${appearanceClasses.animation}`}
                    dangerouslySetInnerHTML={{
                      __html: processYouTubeContent(brandData.description),
                    }}
                  />
                </div>
              )}

              {isMobile && brandData?.program ? (
                <div className={themeLayout.joinCard}>
                  <div
                    className={`${themeStyles.joinCard} ${appearanceClasses.animation} hover:shadow-lg transition-shadow`}
                    style={{
                      backgroundColor: `${colors.secondary}10`,
                      borderColor: `${colors.secondary}30`,
                    }}
                  >
                    {hasJoined ? (
                      <div className="text-center">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg
                            className="w-6 h-6 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                        </div>
                        <h2
                          className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                        >
                          You're an Ambassador!
                        </h2>
                        <p
                          className={`text-muted-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                        >
                          You're an active ambassador for{" "}
                          {brandData.companyName}. Check your dashboard for
                          stats and earnings.
                        </p>
                      </div>
                    ) : hasPendingApplication ? (
                      <div className="text-center">
                        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg
                            className="w-6 h-6 text-yellow-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            ></path>
                          </svg>
                        </div>
                        <h2
                          className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                        >
                          Application Pending
                        </h2>
                        <p
                          className={`text-muted-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                        >
                          You have already applied to join the{" "}
                          {brandData.companyName} affiliate program. We'll
                          notify you once your application is reviewed.
                        </p>
                      </div>
                    ) : (
                      <>
                        {programBadge}
                        <h2
                          className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                        >
                          {appearanceSettings?.joinTitle ||
                            brandData.program?.joinTitle ||
                            appearanceSettings?.joinText ||
                            brandData.program?.joinText ||
                            "Join the team"}
                        </h2>
                        <p
                          className={`text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                        >
                          {(() => {
                            const processedContent = processYouTubeContent(
                              brandData.program?.joinText || ""
                            );
                            return (
                              <div
                                className="prose max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html: processedContent,
                                }}
                              />
                            );
                          })()}
                        </p>
                        <div
                          className={`text-sm text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight} ${appearanceClasses.animation}`}
                        >
                          {brandData.program?.commissionValue}% commission on
                          all code uses
                        </div>
                        <button
                          onClick={handleJoinClick}
                          disabled={isGeneratingCode}
                          className={`w-full text-white py-2 ${appearanceClasses.borderRadius} ${appearanceClasses.animation} ${appearanceClasses.shadow} hover:shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
                          style={{
                            backgroundColor: colors.primary,
                            filter: "brightness(1)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.filter = "brightness(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.filter = "brightness(1)";
                          }}
                        >
                          {isGeneratingCode ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Generating code...
                            </>
                          ) : (
                            <span className="flex items-center gap-2">
                              Join Now
                              {hasPendingInvitation && (
                                <span className="text-xs opacity-90">
                                  • You have pending invitation
                                </span>
                              )}
                            </span>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ):(
                <>
                {!brandData?.program && <div className={themeLayout.joinCard}>
                  <div
                    className={`${appearanceClasses.animation} hover:shadow-lg transition-shadow`}
                    style={{
                      backgroundColor: `${colors.secondary}10`,
                      borderColor: `${colors.secondary}30`,
                    }}
                  >

                  <div className={`${themeStyles.joinCard} ${appearanceClasses.animation} hover:shadow-lg transition-shadow`}
                    style={{
                      backgroundColor: `${colors.secondary}10`,
                      borderColor: `${colors.secondary}30`,
                    }}
                  >
                    <h2
                      className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                     Program Not Found
                    </h2>
                    <p
                      className={`text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      This brand currently has no affiliate program. Please check back later.
                    </p>
                    <button
                      onClick={() => navigate("/auth/login")}
                      className={`text-brand-600 hover:text-brand-700 flex items-center gap-1 mx-auto`}
                    >
                      <ArrowLeft size={16} />
                      Login to discover more brands
                    </button>
                  </div>
                  </div>
                </div>}
                </>
              )}

              {/* Showcase Images Carousel */}
              {appearanceSettings?.imageIds &&
                appearanceSettings.imageIds.length > 0 && (
                  <div
                    className={`${themeStyles.showcaseContainer} ${themeLayout.showcasePosition}`}
                  >
                    <ShowcaseCarousel
                      images={appearanceSettings.imageIds}
                      autoPlay={true}
                      interval={4000}
                      showControls={true}
                      className={`${appearanceClasses.shadow} ${appearanceClasses.borderRadius}`}
                    />
                    {/* Product Showcase */}
                  </div>
                )}

              {/* Product Showcase */}
              {appearanceSettings?.showcaseProductIds &&
                appearanceSettings.showcaseProductIds.length > 0 && (
                  <div className={`${themeLayout.showcasePosition}`}>
                    <ProductShowcase
                      selectedProductIds={appearanceSettings.showcaseProductIds}
                      appearanceSettings={appearanceSettings}
                      className={`${appearanceClasses.shadow} ${appearanceClasses.borderRadius}`}
                    />
                  </div>
                )}
            </div>

            {/* Join Card */}
        {!isMobile && brandData?.program && (
          <div className={themeLayout.joinCard}>
            <div
              className={`${themeStyles.joinCard} ${appearanceClasses.animation} hover:shadow-lg transition-shadow`}
              style={{
                backgroundColor: `${colors.secondary}10`,
                borderColor: `${colors.secondary}30`,
              }}
            >
              {hasJoined ? (
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <h2
                    className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    You're an Ambassador!
                  </h2>
                  <p
                    className={`text-muted-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    You're an active ambassador for {brandData.companyName}.
                    Check your dashboard for stats and earnings.
                  </p>
                </div>
              ) : hasPendingApplication ? (
                <div className="text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-6 h-6 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                  </div>
                  <h2
                    className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    Application Pending
                  </h2>
                  <p
                    className={`text-muted-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    You have already applied to join the {brandData.companyName}{" "}
                    affiliate program. We'll notify you once your application is
                    reviewed.
                  </p>
                </div>
              ) : (
                <>
                  {programBadge}
                  <h2
                    className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    {appearanceSettings?.joinTitle ||
                      brandData.program?.joinTitle ||
                      appearanceSettings?.joinText ||
                      brandData.program?.joinText ||
                      "Join the team"}
                  </h2>
                  <p
                    className={`text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    {(() => {
                      const processedContent = processYouTubeContent(
                        brandData.program?.joinText || ""
                      );
                      return (
                        <div
                          className="prose max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: processedContent,
                          }}
                        />
                      );
                    })()}
                  </p>
                  <div
                    className={`text-sm text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight} ${appearanceClasses.animation}`}
                  >
                    {brandData.program?.commissionValue}% commission on all code
                    uses
                  </div>

                  {/* Supported Payment Methods
                  {supportedPaymentMethods.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">
                        Supported Payout Methods
                      </h3>
                      <div className="flex flex-row flex-wrap gap-2">
                        {supportedPaymentMethods.map((method) => (
                          <div
                            key={method.id}
                            className="flex items-center gap-2 text-sm text-muted-foreground"
                          >
                            <div
                              className="w-4 h-4 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: `${colors.primary}20`,
                              }}
                            >
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{
                                  backgroundColor: colors.primary,
                                }}
                              ></div>
                            </div>
                            {method.paymentMethod.name}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        You'll need to set up at least 2 payment methods to join
                        this brand.
                      </p>
                    </div>
                  )} */}
                  <button
                    onClick={handleJoinClick}
                    disabled={isGeneratingCode}
                    className={`w-full text-white py-2 ${appearanceClasses.borderRadius} ${appearanceClasses.animation} ${appearanceClasses.shadow} hover:shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
                    style={{
                      backgroundColor: colors.primary,
                      filter: "brightness(1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = "brightness(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = "brightness(1)";
                    }}
                  >
                    {isGeneratingCode ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Generating code...
                      </>
                    ) : (
                      <span className="flex items-center gap-2">
                        Join Now
                        {hasPendingInvitation && (
                          <span className="text-xs opacity-90">
                            • You have pending invitation
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        </div>

        {/* Only renders on a brand's own portal — a no-op on reveshare.com. */}
        <PoweredByReveshare />

        {modals}
      </main>

      {/* Enhanced YouTube Embed Styles */}
      <style>{`
        .youtube-embed-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%;
          height: 0;
          margin: 12px 0 !important;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .youtube-embed-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          border-radius: 8px;
        }
        
        @media (max-width: 640px) {
          .youtube-embed-wrapper {
            padding-bottom: 65%;
            margin: 8px 0 !important;
          }
        }
        
        /* Rich Text Formatting Styles */
        .prose {
          color: inherit;
        }
        
        .prose p {
          margin: 0.5em 0;
          color: inherit;
        }
        
        .prose a {
          color: rgb(var(--brand-600));
          text-decoration: underline;
        }
        
        .prose a:hover {
          color: rgb(var(--brand-700));
        }
        
        /* Text Alignment - Handle both inline styles and TipTap classes */
        .prose p[style*="text-align: center"],
        .prose p[style*="text-align: center;"],
        .prose .text-center,
        .prose [style*="text-align: center"] {
          text-align: center !important;
        }
        
        .prose p[style*="text-align: right"],
        .prose p[style*="text-align: right;"],
        .prose .text-right,
        .prose [style*="text-align: right"] {
          text-align: right !important;
        }
        
        .prose p[style*="text-align: left"],
        .prose p[style*="text-align: left;"],
        .prose .text-left,
        .prose [style*="text-align: left"] {
          text-align: left !important;
        }
        
        /* Force text alignment with higher specificity */
        .prose div[style*="text-align: center"] { text-align: center !important; }
        .prose div[style*="text-align: right"] { text-align: right !important; }
        .prose div[style*="text-align: left"] { text-align: left !important; }
        
        .prose span[style*="text-align: center"] { text-align: center !important; }
        .prose span[style*="text-align: right"] { text-align: right !important; }
        .prose span[style*="text-align: left"] { text-align: left !important; }
        
        /* Bullet Points and Lists - Handle TipTap's HTML structure */
        .prose ul,
        .prose ul[data-type="bulletList"] {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        
        .prose ol,
        .prose ol[data-type="orderedList"] {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        
        .prose li,
        .prose li[data-type="listItem"] {
          margin: 0.25em 0;
          color: inherit;
        }
        
        /* Bold and Italic */
        .prose strong,
        .prose b,
        .prose [data-type="bold"] {
          font-weight: 600;
          color: inherit;
        }
        
        .prose em,
        .prose i,
        .prose [data-type="italic"] {
          font-style: italic;
          color: inherit;
        }
        
        /* Headings */
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          font-weight: 600;
          margin: 1em 0 0.5em 0;
          color: inherit;
        }
        
        .prose h1 { font-size: 1.5em; }
        .prose h2 { font-size: 1.25em; }
        .prose h3 { font-size: 1.125em; }
        
        /* Links */
        .prose a[href] {
          color: rgb(var(--brand-600));
          text-decoration: underline;
          word-break: break-word;
        }
        
        .prose a[href]:hover {
          color: rgb(var(--brand-700));
        }
        
        /* Blockquotes */
        .prose blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          color: #6b7280;
        }
        
        /* Code */
        .prose code {
          background-color: #f3f4f6;
          padding: 0.125em 0.25em;
          border-radius: 0.25em;
          font-family: monospace;
          font-size: 0.875em;
        }
        
        /* Horizontal Rule */
        .prose hr {
          border: none;
          border-top: 1px solid #e5e7eb;
          margin: 1.5em 0;
        }
        
        /* Additional TipTap-specific styles */
        .prose [data-type="paragraph"] {
          margin: 0.5em 0;
        }
        
        .prose [data-type="heading"] {
          font-weight: 600;
          margin: 1em 0 0.5em 0;
        }
        
        /* Handle TipTap's text alignment attributes */
        .prose [style*="text-align: center"],
        .prose [style*="text-align: center;"] {
          text-align: center !important;
        }
        
        .prose [style*="text-align: right"],
        .prose [style*="text-align: right;"] {
          text-align: right !important;
        }
        
        .prose [style*="text-align: left"],
        .prose [style*="text-align: left;"] {
          text-align: left !important;
        }
        
        /* Handle TipTap's data attributes for text alignment */
        .prose [data-text-align="center"] {
          text-align: center !important;
        }
        
        .prose [data-text-align="right"] {
          text-align: right !important;
        }
        
        .prose [data-text-align="left"] {
          text-align: left !important;
        }
        
        /* Override any conflicting styles */
        .prose * {
          text-align: inherit;
        }
        
        .prose [style*="text-align"] {
          text-align: inherit !important;
        }
      `}</style>
    </div>
  );
}

export default PublicBrandDetailView;
